<template>
  <v-card class="domain-model-card mb-5 position-relative" id="domain-model">
    <div>
      <v-toolbar flat color="white" height="62" @click.stop @mousedown.stop>
        <v-col cols="4" class="pt-3 d-flex subheading font-weight-bold align-center">
          <div class="mx-2 d-flex align-center text-no-wrap">
            <v-icon color="primary">mdi-alpha-d-box</v-icon>&nbsp;
            Domain Model
          </div>
          <help-dialog-workflow-aggregates
            v-slot="{ openDialog }"
            content-class="dialog-top-right"
          >
            <v-btn variant="text" icon="mdi-help-circle-outline" @click="openDialog" class="mr-2"></v-btn>
          </help-dialog-workflow-aggregates>
          <v-tabs
            v-if="!workflow.boundedContexts?.length"
            v-model="globalActiveTab"
            density="compact"
            height="34"
            bg-color="transparent"
            class="domain-model-global-tabs ml-4"
          >
            <v-tab value="diagram" :ripple="false">
              <v-icon size="x-large" class="mr-2">mdi-file-tree</v-icon>
              <span>Domain View</span>
            </v-tab>
            <v-tooltip location="top" open-delay="300">
              <template v-slot:activator="{ props }">
                <div v-bind="props" class="cursor-not-allowed">
                  <v-tab value="spec" :ripple="false" :disabled="true" class="disabled-tab">
                    <v-icon size="x-large" class="mr-2">mdi-api</v-icon>
                    <span>Push Spec</span>
                  </v-tab>
                </div>
              </template>
              <span>In order to access Push Spec, you need to add a bounded context to at least one entity.</span>
            </v-tooltip>
            <v-tooltip location="top" open-delay="300">
              <template v-slot:activator="{ props }">
                <div v-bind="props" class="cursor-not-allowed">
                  <v-tab value="code" :ripple="false" :disabled="true" class="disabled-tab">
                    <v-icon size="x-large" class="mr-2">mdi-code-block-tags</v-icon>
                    <span>Code View</span>
                    <v-chip size="x-small" class="ml-2" label>
                      Beta
                    </v-chip>
                  </v-tab>
                </div>
              </template>
              <span>In order to access the code view, you need to add a bounded context to at least one entity.</span>
            </v-tooltip>
          </v-tabs>
        </v-col>
        <v-spacer />
        <v-tooltip location="top" open-delay="100">
          <template v-slot:activator="{ props }">
            <div id="domain-model-event-selector" class="event-select-wrapper" v-bind="props">
              <event-select width="100%" :eventId="route.params.eventId" />
            </div>
          </template>
          <span>Select event to filter Domain Model</span>
        </v-tooltip>
        <v-checkbox
          v-model="domainModelShowReadModels"
          @click="toggleDomainModelShowReadModels()"
          label="Show read models"
          hide-details
          class="mx-4"
        />
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <span v-bind="props">
              <v-checkbox
                v-model="domainModelShowEntities"
                @click="toggleDomainModelShowEntities()"
                label="Show data fields"
                hide-details
                class="mx-4"
              ></v-checkbox>
            </span>
          </template>
          <span>Show related entities</span>
        </v-tooltip>
        <v-checkbox
          :model-value="domainModelShowEventDetails"
          @update:model-value="toggleDomainModelShowEventDetails()"
          label="Show domain event details"
          hide-details
          class="mx-4"
        />
      </v-toolbar>

      <template v-for="(boundedContext, boundedContextIndex) in boundedContexts" :key="boundedContextIndex">
        <div v-if="boundedContext.name !== null" class="bounded-context mx-5 mt-5 mb-10">
          <v-tabs
            class="bounded-context-tabs align-center"
            v-model="boundedContextTabs[boundedContext.name]"
            density="compact"
            height="48"
            bg-color="grey-lighten-4"
            @click.stop
            @mousedown.stop
          >
            <v-text-field
              v-if="renameBoundedContextId === boundedContext.id"
              class="mx-5 pa-0"
              autofocus
              hide-details
              :rules="[value => (value.length <= 40) || 'Max 40 characters']"
              v-model="renameBoundedContextName"
              v-on:keyup.enter="$event.target.blur()"
              @focus="$event.target.select()"
              @blur="renameBoundedContext()"
              @keydown.stop
              @click.stop
              @mousedown.stop
              variant="outlined"
              density="compact"
            />
            <h3
              v-else
              class="px-5 align-self-center"
              @click="startRenameBoundedContext(boundedContext)"
            >
              {{ boundedContext.name }}
            </h3>

            <div v-show="renameBoundedContextId !== boundedContext.id">
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-tab v-bind="props" value="diagram" :ripple="false">
                    <v-icon size="x-large" class="mr-2">mdi-file-tree</v-icon>
                    <span>Domain View</span>
                  </v-tab>
                </template>

                <span>
                  Click here to view a diagram of your domain model
                </span>
              </v-tooltip>
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-tab
                    v-bind="props"
                    value="spec"
                    :ripple="false"
                    :disabled="!boundedContext.systems?.length"
                    :class="{ 'disabled-tab': !boundedContext.systems?.length }"
                  >
                    <v-icon size="x-large" class="mr-2">mdi-api</v-icon>
                    <span>Push Spec</span>
                  </v-tab>
                </template>

                <span>
                  Push the domain model specification to GitHub
                </span>
              </v-tooltip>
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-tab
                    v-bind="props"
                    value="code"
                    :ripple="false"
                    :disabled="!boundedContext.systems?.length || store.state.workflow.isViewOnlyEnabled"
                    :class="{ 'disabled-tab': !boundedContext.systems?.length || store.state.workflow.isViewOnlyEnabled }"
                  >
                    <v-icon size="x-large" class="mr-2">mdi-code-block-tags</v-icon>
                    <span>Code View</span>
                    <v-chip size="x-small" class="ml-2" label>
                      Beta
                    </v-chip>
                  </v-tab>
                </template>

                <span>
                  Click here to generate API code and documentation for the domain model in this bounded context
                </span>
              </v-tooltip>
              <help-dialog-code-view
                v-if="boundedContextTabs[boundedContext.name] === 'code'"
                v-slot="{ openDialog }"
              >
                <v-btn variant="text" icon="mdi-help-circle-outline" @click="openDialog" class="ml-2"></v-btn>
              </help-dialog-code-view>
            </div>
            <v-spacer></v-spacer>
            <h4 style="user-select: none;" class="text-right mx-5 text-grey align-self-center">Bounded Context</h4>
          </v-tabs>
          <div v-if="boundedContextTabs[boundedContext.name] === 'code'">
            <workflow-aggregates-ai-code
              :boundedContext="boundedContext"
              @navigateToDomainView="(issue) => switchToDomainView(boundedContext.name, issue)"
            />
          </div>

          <div v-else-if="boundedContextTabs[boundedContext.name] === 'spec'">
            <push-specification
              :boundedContext="boundedContext"
              @navigateToDomainView="(issue) => switchToDomainView(boundedContext.name, issue)"
            />
          </div>

          <div v-else>
            <div v-if="!boundedContext.systems?.length" class="pa-5 d-flex">
              <span class="pt-3">No aggregates in this bounded context</span>
              <v-spacer></v-spacer>
              <v-btn
                icon="mdi-delete"
                @click="confirmDeleteBoundedContext(boundedContext.id)"
                :disabled="$store.state.isWorkflowDisabled"
              />
            </div>
            <domain-model-system-table
              v-else
              :boundedContext="boundedContext"
              :showEntities="domainModelShowEntities"
              :showReadModels="domainModelShowReadModels"
              :showEventDetails="domainModelShowEventDetails"
              :refreshTrigger="refreshKey"
            />
          </div>
        </div>
        <div v-if="boundedContext.name === null" class="domain-model-without-context ma-5">
          <domain-model-system-table
            :boundedContext="boundedContext"
            :showEntities="domainModelShowEntities"
            :showReadModels="domainModelShowReadModels"
            :showEventDetails="domainModelShowEventDetails"
            :refreshTrigger="refreshKey"
          />
        </div>
      </template>
      <v-dialog v-model="showDeleteDialog" max-width="500">
        <v-card>
          <v-card-title>Confirm Delete</v-card-title>
          <v-card-text>
            Are you sure you want to delete this bounded context?
            Any code generated for this bounded context will be deleted.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showDeleteDialog = false; deleteBoundedContext(deleteBoundedContextId)">
              Delete
            </v-btn>
            <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-card>
</template>

<script setup>
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import EventSelect from '@/components/EventSelect'
import HelpDialogCodeView from '@/components/HelpDialogCodeView'
import DomainModelSystemTable from '@/components/DomainModelSystemTable'
import WorkflowAggregatesAiCode from '@/components/WorkflowAggregatesAICode'
import PushSpecification from '@/components/PushSpecification.vue'
import HelpDialogWorkflowAggregates from '@/components/HelpDialogWorkflowAggregates'
import randomUUID from '@/utils/uuid'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import useDomainModel from '@/composables/useDomainModel'

const refreshKey = ref(0)
const showDeleteDialog = ref(false)
const boundedContextTabs = ref({})
const deleteBoundedContextId = ref(null)
const renameBoundedContextId = ref(null)
const renameBoundedContextName = ref('')
const globalActiveTab = ref('diagram')
const highlightTimeout = ref(null)

const route = useRoute()
const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const showSidePanel = computed(() => store.state.showSidePanel)
const domainModelShowEntities = computed(() => store.state.domainModelShowEntities)
const domainModelShowReadModels = computed(() => store.state.domainModelShowReadModels)
const domainModelShowEventDetails = computed(() => store.state.domainModelShowEventDetails)
const svgGraph = computed(() => safeStructuredClone(store.getters['svgGraphData/svgGraphData']))
const events = computed(() => (workflow.value.domainEvents || []).filter((event) => event.type === 'bpmn:Task'))
const sortedEvents = computed(() => {
  return [...events.value].sort((a, b) => svgGraph.value[a.id].x - svgGraph.value[b.id].x)
})

const { getSchemaFieldsForCard } = useDomainModel()

const systems = computed(() => {
  const entities = []
  const { eventId } = route.params
  const filteredEvents = eventId ? sortedEvents.value.filter((event) => event.id === eventId) : sortedEvents.value

  for (const event of filteredEvents) {
    const entityCard = cardHelpers.methods.getEntityCard(event)
    let commandCard = cardHelpers.methods.getCommandCard(event)
    let queryCards = cardHelpers.methods.getQueryCards(event)
    let domainEventCard = cardHelpers.methods.getDomainEventCard(event)
    const rootEntity = entityCard?.schemaId
      ? workflow.value.schemas?.find((entity) => entity.id === entityCard.schemaId)
      : null

    const boundedContext = rootEntity ? rootEntity.boundedContext : entityCard?.boundedContext

    if (commandCard) {
      // adding eventId to be able to link to the event when generating code
      commandCard = { ...commandCard, eventId: event.id, schemaData: getSchemaFieldsForCard(event, commandCard.id) }
    }
    // adding eventId to be able to link to the event when generating code
    queryCards = queryCards.map((card) => {
      return { ...card, eventId: event.id, schemaData: getSchemaFieldsForCard(event, card.id) }
    })
    if (domainEventCard) {
      domainEventCard = { ...domainEventCard, eventId: event.id, schemaData: getSchemaFieldsForCard(event, domainEventCard.id) }
    }
    const index = entities.findIndex((item) => {
      if (!entityCard || !item.groupedBy || (boundedContext !== item.groupedBy.boundedContext)) {
        return false
      }

      if (item.groupedBy.type === 'linked-entity' && rootEntity) {
        return item.groupedBy.rootEntity.id === rootEntity.id
      }

      if (item.groupedBy.type === 'unlinked-text' && entityCard && entityCard.description) {
        return item.groupedBy.text === sanitizeCardDescription(entityCard.description)
      }

      return false
    })

    const laneIndex = workflow.value.lanes.findIndex((lane) => lane.id === event.laneId)
    const prevRole = Object.assign({ laneIndex }, workflow.value.lanes[laneIndex])

    if (index >= 0) {
      // group on prevRole inside one system, if the prevRole is in this
      // system, insert data after the prevRole otherwise insert at the end
      const roleIndex = entities[index].roles.findLastIndex((item) => item.id === prevRole.id)
      if (roleIndex >= 0) {
        entities[index].actions.splice(roleIndex + 1, 0, commandCard)
        entities[index].queries.splice(roleIndex + 1, 0, queryCards)
        entities[index].domainEventCards.splice(roleIndex + 1, 0, domainEventCard)
        entities[index].events.splice(roleIndex + 1, 0, event)
        entities[index].roles.splice(roleIndex + 1, 0, prevRole)
      } else {
        entities[index].actions.push(commandCard)
        entities[index].queries.push(queryCards)
        entities[index].domainEventCards.push(domainEventCard)
        entities[index].events.push(event)
        entities[index].roles.push(prevRole)
      }

      if (entityCard) {
        entities[index].entityCards.push(entityCard)
      }
    } else {
      entities.push({
        id: randomUUID(),
        actions: commandCard ? [commandCard] : [null],
        queries: [queryCards],
        domainEventCards: [domainEventCard],
        events: event ? [event] : [],
        entityCards: entityCard ? [entityCard] : [],
        roles: [prevRole],
        groupedBy: workflowHelpers.methods.getSystemGroupedBy(rootEntity, entityCard)
      })
    }
  }

  return entities
})

const boundedContexts = computed(() => {
  const result = safeStructuredClone(workflow.value.boundedContexts || [])
  const notInBC = { name: null, systems: [] }
  const bcMap = {}
  result.forEach((it) => {
    bcMap[it.id] = it
  })

  systems.value.forEach((system, systemIndex) => {
    let bc = bcMap[system.groupedBy?.boundedContext]
    if (!bc) {
      bc = notInBC
    }

    if (!bc.systems) {
      bc.systems = []
    }

    const mappedSystem = {
      ...system,
      events: system.events.map((event, index) => ({ ...event, index })),
      index: systemIndex
    }

    bc.systems.push(mappedSystem)
  })

  // Place systems that don't have a bounded context at the bottom
  result.push(notInBC)

  return (route.params.eventId ? result.filter((bc) => !!bc.systems) : result).sort((a, b) => {
    if (a.name === null) {
      // Put the systems without a bounded context at the bottom
      return 1
    }

    if (!a.systems?.length || !b.systems?.length) {
      // Do not sort empty bounded contexts
      return 0
    }

    return svgGraph.value[a.systems[0].events[0].id].x - svgGraph.value[b.systems[0].events[0].id].x
  })
})

const confirmDeleteBoundedContext = (id) => {
  showDeleteDialog.value = true
  deleteBoundedContextId.value = id
}

const deleteBoundedContext = async (id) => {
  const bc = workflow.value.boundedContexts.find((it) => it.id === id)
  if (bc) {
    const name = bc.name
    const boundedContexts = workflow.value.boundedContexts.filter((it) => it.id !== id)
    // TODO: delete all the files associated with this!
    await store.dispatch('boundedContexts/deleteBoundedContext', id)
    await store.dispatch('workflow/updateWorkflow', {
      projectId: workflow.value.projectId,
      input: {
        id: workflow.value.id,
        expectedVersion: workflow.value.version,
        boundedContexts,
        updateNote: {
          action: 'deleted bounded context',
          target: name
        }
      }
    })
  }
}

const startRenameBoundedContext = (boundedContext) => {
  if (store.state.workflow.isViewOnlyEnabled) {
    return
  }
  renameBoundedContextId.value = boundedContext.id
  renameBoundedContextName.value = boundedContext.name
}

const renameBoundedContext = () => {
  const bcIndex = workflow.value.boundedContexts.findIndex((it) => it.id === renameBoundedContextId.value)
  let bc = null
  if (bcIndex >= 0) {
    bc = workflow.value.boundedContexts[bcIndex]
  }

  if (bc && renameBoundedContextName.value && bc.name !== renameBoundedContextName.value) {
    if (workflow.value.boundedContexts.find((it) => it.name === renameBoundedContextName.value)) {
      store.commit('snackbar/showMessage', {
        content: 'There is already a bounded context named: ' + renameBoundedContextName.value,
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
    } else {
      const newBoundedContexts = workflow.value.boundedContexts.filter((it) => it.id !== bc.id)
      newBoundedContexts.splice(bcIndex, 0, { ...bc, name: renameBoundedContextName.value })

      store.dispatch('workflow/updateWorkflow', {
        projectId: workflow.value.projectId,
        input: {
          id: workflow.value.id,
          expectedVersion: workflow.value.version,
          boundedContexts: newBoundedContexts,
          updateNote: {
            action: 'renamed bounded context',
            target: renameBoundedContextName.value
          }
        }
      })

      // Update boundedContextTabs
      boundedContextTabs.value[renameBoundedContextName.value] = boundedContextTabs.value[bc.name]
      delete boundedContextTabs.value[bc.name]
    }
  }
  renameBoundedContextId.value = null
  renameBoundedContextName.value = ''
}

const toggleDomainModelShowEntities = () => {
  store.commit('setDomainModelShowEntities', !domainModelShowEntities.value)
}

const toggleDomainModelShowReadModels = () => {
  store.commit('setDomainModelShowReadModels', !domainModelShowReadModels.value)
}

const toggleDomainModelShowEventDetails = () => {
  store.commit('setDomainModelShowEventDetails', !domainModelShowEventDetails.value)
}

const switchToDomainView = (boundedContextName, issue) => {
  boundedContextTabs.value[boundedContextName] = 'diagram'

  if (issue) {
    if (issue.field && !domainModelShowEntities.value) {
      store.commit('setDomainModelShowEntities', true)
    }

    nextTick(() => {
      setTimeout(() => {
        scrollToAndHighlightElement(issue)
      }, 250)
    })
  }
}

const findEventForCard = (cardId) => {
  for (const event of workflow.value.domainEvents || []) {
    if (event.cards?.some((c) => c.id === cardId)) {
      return event
    }
  }
  return null
}

const findSchemaFieldIndices = (schemaId, fieldPath) => {
  if (!schemaId || !fieldPath) {
    return null
  }
  const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
  if (!schema) {
    return null
  }

  const segments = fieldPath.split('.')
  const indices = []
  let currentFields = schema.fields

  for (const segment of segments) {
    const idx = currentFields?.findIndex((f) => f.name === segment) ?? -1
    if (idx < 0) {
      return null
    }
    indices.push(idx)
    currentFields = currentFields[idx].fields
  }

  return { indices }
}

const getElementIdFromIssue = (issue) => {
  if (issue.type === 'event') {
    for (const bc of boundedContexts.value) {
      for (const system of bc.systems || []) {
        const event = system.events?.find((e) => e.id === issue.id)
        if (event) {
          return `event-${system.index}-${event.index}`
        }
      }
    }
    return null
  }

  if (issue.type === 'role') {
    for (const bc of boundedContexts.value) {
      for (const system of bc.systems || []) {
        if (system.roles?.some((r) => r.id === issue.id)) {
          return `role-${system.index}-${issue.id}`
        }
      }
    }
    return null
  }

  if (issue.type === 'entity') {
    const result = issue.field ? findSchemaFieldIndices(issue.id, issue.field) : null
    if (result) {
      const rootEntityId = `root-entity-${issue.id}-field-${result.indices[0]}`
      if (document.getElementById(rootEntityId)) {
        return rootEntityId
      }
      return `entity-${issue.id}-field-${result.indices[0]}`
    }
    return issue.id
  }

  if (issue.type === 'command' || issue.type === 'readmodel') {
    const event = findEventForCard(issue.id)
    if (!event) {
      return null
    }
    if (issue.field) {
      const card = event.cards?.find((c) => c.id === issue.id)
      const result = findSchemaFieldIndices(card?.schemaId, issue.field)
      if (result) {
        return buildCardFieldElementId(issue.type, event.id, result.indices)
      }
    }
    return null
  }

  return null
}

const buildCardFieldElementId = (issueType, eventId, indices) => {
  if (issueType === 'command') {
    return `write-model-${eventId}-field-${indices.join('-')}`
  }
  // Read-model UI renders only depth 1 and depth 2; clamp deeper paths to the depth-2 parent.
  if (indices.length === 1) {
    return `read-model-${eventId}-field-${indices[0]}`
  }
  return `read-model-${eventId}-field-${indices[0]}-entity-${indices[1]}`
}

const getHighlightClass = (issueType) => {
  const classMap = {
    entity: 'validation-highlight-entity',
    command: 'validation-highlight-command',
    readmodel: 'validation-highlight-readmodel',
    event: 'validation-highlight-event',
    role: 'validation-highlight-role'
  }
  return classMap[issueType] || 'validation-highlight-command'
}

const findElementForIssue = (issue) => {
  const elementId = getElementIdFromIssue(issue)
  if (elementId) {
    const byId = document.getElementById(elementId)
    if (byId) {
      return byId
    }
  }
  if (issue.type === 'readmodel' || issue.type === 'command') {
    return document.querySelector(`#domain-model [data-card-id="${CSS.escape(issue.id)}"]`)
  }
  return null
}

const scrollToAndHighlightElement = (issue) => {
  const element = findElementForIssue(issue)
  if (!element) {
    return
  }

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  })

  setTimeout(() => {
    const container = document.getElementById('domain-model')
    if (container) {
      container.scrollTop = Math.max(0, container.scrollTop - 100)
    }
  }, 300)

  const highlightClass = getHighlightClass(issue.type)
  element.classList.add(highlightClass)

  if (highlightTimeout.value) {
    clearTimeout(highlightTimeout.value)
  }
  highlightTimeout.value = setTimeout(() => {
    element.classList.remove(highlightClass)
  }, 2500)
}

watch([() => showSidePanel, () => workflow.value.version, () => domainModelShowEntities, () => systems], async () => {
  await nextTick()
  refreshKey.value++
})

watch(() => boundedContextTabs, async () => {
  await nextTick()
  refreshKey.value++
}, { deep: true })

onBeforeUnmount(() => {
  if (highlightTimeout.value) {
    clearTimeout(highlightTimeout.value)
  }
})

</script>

<style scoped>
.domain-model-card {
  margin-top: 40px;
  overflow: visible !important;
}

.domain-model-without-context {
  overflow: clip;
}

:deep(.domain-model-global-tabs) {
  flex-shrink: 0;

  .v-slide-group__content {
    gap: 4px
  }
}

.bounded-context {
  background-color: rgb(251, 251, 251);
  border: 2px solid rgb(54, 50, 50);
  border-radius: 10px;
  overflow: clip;
}

.bounded-context-tabs .v-tab {
  margin: 0 2px;
  padding: 0;
  width: 32px;
  min-width: 32px;
}

.event-select-wrapper {
  flex-grow: 1;
  max-width: 300px;
}

.v-tab.disabled-tab {
  opacity: 0.6;
}

:deep(.validation-highlight-command) {
  animation: validation-blink-blue 2.4s ease-in-out;
  position: relative;
  z-index: 10;
}

:deep(.validation-highlight-readmodel) {
  animation: validation-blink-green 2.4s ease-in-out;
  position: relative;
  z-index: 10;
}

:deep(.validation-highlight-entity) {
  animation: validation-blink-yellow 2.4s ease-in-out;
  position: relative;
  z-index: 10;
}

:deep(.validation-highlight-event) {
  animation: validation-blink-orange 2.4s ease-in-out;
  position: relative;
  z-index: 10;
}

:deep(.validation-highlight-role) {
  animation: validation-blink-purple 2.4s ease-in-out;
  position: relative;
  z-index: 10;
}

@keyframes validation-blink-blue {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
    background-color: transparent;
  }
  8%, 25% {
    box-shadow: 0 0 12px 3px rgba(33, 150, 243, 0.5);
    background-color: rgba(33, 150, 243, 0.15);
  }
  33% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
    background-color: transparent;
  }
  41%, 58% {
    box-shadow: 0 0 12px 3px rgba(33, 150, 243, 0.5);
    background-color: rgba(33, 150, 243, 0.15);
  }
  66% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
    background-color: transparent;
  }
  74%, 91% {
    box-shadow: 0 0 12px 3px rgba(33, 150, 243, 0.5);
    background-color: rgba(33, 150, 243, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
    background-color: transparent;
  }
}

@keyframes validation-blink-green {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
  8%, 25% {
    box-shadow: 0 0 12px 3px rgba(76, 175, 80, 0.7);
  }
  33% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
  41%, 58% {
    box-shadow: 0 0 12px 3px rgba(76, 175, 80, 0.7);
  }
  66% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
  74%, 91% {
    box-shadow: 0 0 12px 3px rgba(76, 175, 80, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

@keyframes validation-blink-yellow {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    background-color: transparent;
  }
  8%, 25% {
    box-shadow: 0 0 12px 3px rgba(255, 193, 7, 0.5);
    background-color: rgba(255, 193, 7, 0.15);
  }
  33% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    background-color: transparent;
  }
  41%, 58% {
    box-shadow: 0 0 12px 3px rgba(255, 193, 7, 0.5);
    background-color: rgba(255, 193, 7, 0.15);
  }
  66% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    background-color: transparent;
  }
  74%, 91% {
    box-shadow: 0 0 12px 3px rgba(255, 193, 7, 0.5);
    background-color: rgba(255, 193, 7, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    background-color: transparent;
  }
}

@keyframes validation-blink-purple {
  0% {
    box-shadow: 0 0 0 0 rgba(156, 39, 176, 0);
    background-color: transparent;
  }
  8%, 25% {
    box-shadow: 0 0 12px 3px rgba(156, 39, 176, 0.5);
    background-color: rgba(156, 39, 176, 0.15);
  }
  33% {
    box-shadow: 0 0 0 0 rgba(156, 39, 176, 0);
    background-color: transparent;
  }
  41%, 58% {
    box-shadow: 0 0 12px 3px rgba(156, 39, 176, 0.5);
    background-color: rgba(156, 39, 176, 0.15);
  }
  66% {
    box-shadow: 0 0 0 0 rgba(156, 39, 176, 0);
    background-color: transparent;
  }
  74%, 91% {
    box-shadow: 0 0 12px 3px rgba(156, 39, 176, 0.5);
    background-color: rgba(156, 39, 176, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(156, 39, 176, 0);
    background-color: transparent;
  }
}

@keyframes validation-blink-orange {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
    background-color: transparent;
  }
  8%, 25% {
    box-shadow: 0 0 12px 3px rgba(255, 152, 0, 0.5);
    background-color: rgba(255, 152, 0, 0.15);
  }
  33% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
    background-color: transparent;
  }
  41%, 58% {
    box-shadow: 0 0 12px 3px rgba(255, 152, 0, 0.5);
    background-color: rgba(255, 152, 0, 0.15);
  }
  66% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
    background-color: transparent;
  }
  74%, 91% {
    box-shadow: 0 0 12px 3px rgba(255, 152, 0, 0.5);
    background-color: rgba(255, 152, 0, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
    background-color: transparent;
  }
}
</style>

<style>
[data-theme="dark"] .domain-model-card,
[data-theme="dark"] .domain-model-card.v-card {
  background-color: var(--light-1) !important;
  color: var(--text-dark);
}

[data-theme="dark"] .domain-model-card .v-toolbar.bg-white {
  background-color: var(--light-1) !important;
  color: var(--text-dark) !important;
}

[data-theme="dark"] .domain-model-card .bounded-context {
  background-color: var(--light-1);
  border-color: var(--light-4);
}

[data-theme="dark"] .domain-model-card .bounded-context-tabs.bg-grey-lighten-4 {
  background-color: var(--light-2) !important;
  color: var(--text-dark) !important;
}

[data-theme="dark"] .domain-model-card .bounded-context-tabs h3 {
  color: var(--text-dark);
}
</style>
